import React from 'react';
import { Grid, Paper, Typography, Card, CardHeader, CardContent, Chip, Box, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SchoolIcon from '@mui/icons-material/School';
import DashboardSelector from './DashboardSelector';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#F5F5F5',
  minHeight: '100vh',
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
}));

const DataTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: '0.5rem 1rem',
    height: '32px',
    fontSize: '0.875rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#064d51',
    color: '#ffffff',
    fontWeight: 600,
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: 'rgba(192, 242, 243, 0.1)',
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: '#064d51',
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const TrendIndicator = styled(Box)(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  color: trend === 'up' ? '#27ae60' : '#e74c3c',
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const ProgressLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.75rem',
  marginBottom: theme.spacing(0.5),
}));

// API integration function - Replace with actual API call in production
const fetchTrainingDashboardData = async () => {
  // In production, this would be an API call to fetch real data
  // Example: return await axios.get('/api/dashboard/training', { headers: { Authorization: `Bearer ${token}` } });
  
  // For now, return sample data
  return {
    // Training Overview
    activePrograms: 5,
    enrolledEmployees: 78,
    completionRate: 68,
    certificationCoverage: 82,
    
    // Course Status
    courseStatus: [
      { status: 'Not Started', count: 24, color: '#3498db' },
      { status: 'In Progress', count: 35, color: '#f39c12' },
      { status: 'Completed', count: 78, color: '#27ae60' },
      { status: 'Expired', count: 7, color: '#e74c3c' }
    ],
    
    // Course Types
    courseTypes: [
      { type: 'Compliance', count: 45, color: '#064d51' },
      { type: 'Technical', count: 32, color: '#0a7c82' },
      { type: 'Soft Skills', count: 28, color: '#c0f2f3' },
      { type: 'Safety', count: 39, color: '#3498db' }
    ],
    
    // Department Performance
    departmentPerformance: {
      labels: ['HR', 'Finance', 'IT', 'Operations', 'Sales', 'Customer Service'],
      datasets: [
        {
          label: 'Completion Rate (%)',
          data: [92, 78, 86, 65, 72, 59],
          backgroundColor: '#064d51',
        }
      ]
    },
    
    // Monthly completion trends
    monthlyCompletions: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
      datasets: [
        {
          label: 'Course Completions',
          data: [12, 19, 15, 22, 25],
          borderColor: '#064d51',
          backgroundColor: 'rgba(6, 77, 81, 0.1)',
          tension: 0.4,
        }
      ]
    },
    
    // Required vs Optional Completion
    trainingTypeCompletion: {
      labels: ['Required', 'Optional'],
      datasets: [
        {
          label: 'Completion Rate (%)',
          data: [87, 42],
          backgroundColor: ['#064d51', '#3498db'],
        }
      ]
    },
    
    // Upcoming Training Sessions
    upcomingSessions: [
      { courseTitle: 'Workplace Safety Training', date: '2025-06-05', instructor: 'Mohammed Khalid', duration: '2 hours', requiredFor: 'All Employees' },
      { courseTitle: 'Project Management Basics', date: '2025-06-10', instructor: 'Sarah Johnson', duration: '4 hours', requiredFor: 'Team Leads' },
      { courseTitle: 'Compliance Update', date: '2025-06-12', instructor: 'Ahmed Al-Sayed', duration: '1 hour', requiredFor: 'All Employees' },
      { courseTitle: 'Technical Skills Workshop', date: '2025-06-15', instructor: 'Fatima Hassan', duration: '6 hours', requiredFor: 'IT Department' },
      { courseTitle: 'Customer Service Excellence', date: '2025-06-20', instructor: 'Thomas Wright', duration: '3 hours', requiredFor: 'Customer Service Team' }
    ],
    
    // Expiring Certifications
    expiringCertifications: [
      { employeeName: 'Ahmed Saleh', certification: 'Project Management', expiryDate: '2025-06-15', daysRemaining: 21 },
      { employeeName: 'Sarah Johnson', certification: 'Data Security', expiryDate: '2025-06-08', daysRemaining: 14 },
      { employeeName: 'Mohammed Al-Ali', certification: 'Health & Safety', expiryDate: '2025-06-02', daysRemaining: 8 },
      { employeeName: 'Thomas Wright', certification: 'First Aid', expiryDate: '2025-05-29', daysRemaining: 4 },
      { employeeName: 'Fatima Hassan', certification: 'Compliance Officer', expiryDate: '2025-05-28', daysRemaining: 3 }
    ],
    
    // Top Performing Employees
    topPerformers: [
      { employeeName: 'Sarah Johnson', department: 'HR', coursesCompleted: 12, averageScore: 96 },
      { employeeName: 'Mohammed Al-Ali', department: 'IT', coursesCompleted: 10, averageScore: 94 },
      { employeeName: 'Fatima Hassan', department: 'Finance', coursesCompleted: 9, averageScore: 92 },
      { employeeName: 'Thomas Wright', department: 'Sales', coursesCompleted: 8, averageScore: 91 },
      { employeeName: 'Ahmed Saleh', department: 'Operations', coursesCompleted: 8, averageScore: 89 }
    ]
  };
};

// Chart configurations
const getTrainingDashboardCharts = (data) => {
  return {
    courseStatusChart: {
      labels: data.courseStatus.map(item => item.status),
      datasets: [
        {
          data: data.courseStatus.map(item => item.count),
          backgroundColor: data.courseStatus.map(item => item.color),
          borderWidth: 0,
        }
      ]
    },
    courseTypesChart: {
      labels: data.courseTypes.map(item => item.type),
      datasets: [
        {
          data: data.courseTypes.map(item => item.count),
          backgroundColor: data.courseTypes.map(item => item.color),
          borderWidth: 0,
        }
      ]
    }
  };
};

// Dashboard component
const TrainingDashboard = () => {
  const [dashboardData, setDashboardData] = React.useState(null);
  const [charts, setCharts] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchTrainingDashboardData();
        setDashboardData(data);
        setCharts(getTrainingDashboardCharts(data));
      } catch (error) {
        console.error('Error loading training dashboard data:', error);
        // In production, implement proper error handling
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  if (loading || !dashboardData) {
    return (
      <DashboardContainer>
        <DashboardSelector />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>Loading dashboard data...</Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  return (
    <DashboardContainer>
      <DashboardSelector />
      <Typography variant="h5" sx={{ my: 2, color: '#064d51', fontWeight: 600 }}>
        Training Programs Dashboard
      </Typography>
      
      {/* Top Section - Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{dashboardData.activePrograms}</MetricValue>
            <MetricLabel>Active Training Programs</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">1 more than last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{dashboardData.enrolledEmployees}</MetricValue>
            <MetricLabel>Enrolled Employees</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">5 more than last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <MetricValue>{dashboardData.completionRate}%</MetricValue>
              <MetricLabel>Overall Completion Rate</MetricLabel>
              <ProgressContainer>
                <ProgressLabel>
                  <span>Progress</span>
                  <span>{dashboardData.completionRate}%</span>
                </ProgressLabel>
                <LinearProgress 
                  variant="determinate" 
                  value={dashboardData.completionRate} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 5,
                    backgroundColor: 'rgba(192, 242, 243, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#064d51',
                    }
                  }} 
                />
              </ProgressContainer>
            </Box>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MetricValue>{dashboardData.certificationCoverage}%</MetricValue>
              <SchoolIcon sx={{ color: '#064d51', ml: 1 }} />
            </Box>
            <MetricLabel>Certification Coverage</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">4% increase from last quarter</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
      </Grid>
      
      {/* Middle Section - Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartCard>
            <CardHeader
              title="Monthly Course Completions"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Line
                data={dashboardData.monthlyCompletions}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardHeader
              title="Course Enrollment Status"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Pie
                data={charts.courseStatusChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                      },
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Course Types"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Pie
                data={charts.courseTypesChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                      },
                    },
                  },
                }}
                height={180}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Department Completion Rates"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Bar
                data={dashboardData.departmentPerformance}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Completion %',
                      },
                    },
                  },
                }}
                height={180}
              />
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
      
      {/* Bottom Section - Tables */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Upcoming Training Sessions"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Training</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Required For</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.upcomingSessions.map((session, index) => (
                      <TableRow key={index}>
                        <TableCell>{session.courseTitle}</TableCell>
                        <TableCell>{session.date}</TableCell>
                        <TableCell>{session.duration}</TableCell>
                        <TableCell>
                          <Chip 
                            label={session.requiredFor}
                            size="small"
                            sx={{ 
                              backgroundColor: '#064d51',
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Expiring Certifications"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Certification</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell align="right">Days Left</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.expiringCertifications.map((cert, index) => (
                      <TableRow key={index}>
                        <TableCell>{cert.employeeName}</TableCell>
                        <TableCell>{cert.certification}</TableCell>
                        <TableCell>{cert.expiryDate}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={cert.daysRemaining} 
                            size="small"
                            sx={{ 
                              backgroundColor: cert.daysRemaining <= 7 ? '#e74c3c' : 
                                              cert.daysRemaining <= 14 ? '#f39c12' : '#27ae60',
                              color: 'white',
                              fontWeight: 500,
                              minWidth: '32px'
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
    </DashboardContainer>
  );
};

export default TrainingDashboard;
